import { Edit, Map as MapIcon, Package, Repeat, Truck } from "lucide-react"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { formatCost, type priceRoute, resolveRouteKind } from "@/lib/routeCostCalculation"
import {
  ROUTE_KIND_BADGE_CLASSES,
  getRouteKindBadge,
  getRouteKindRowClasses,
} from "@/lib/routeKindDisplay"
import { getPlannedRouteStatusDisplay } from "@/lib/statusDisplay"
import { ROW_HEIGHT } from "@/lib/timelineScale"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type { PlannedRoute } from "@/types/collections"

export function PlanningRouteListItem({
  route,
  operator,
  isFirstStandby,
  isHighlighted,
  cost,
  loadFactor,
  onEdit,
  onShowMap,
}: {
  route: PlannedRoute
  /** Who runs it on the date shown, which a handover can make someone other than the plan's. */
  operator: string | undefined
  isFirstStandby: boolean
  isHighlighted: boolean
  cost: ReturnType<typeof priceRoute> | undefined
  loadFactor: number | undefined
  /** Absent for a login that cannot edit: the row then opens the route's map instead. */
  onEdit?: (route: PlannedRoute) => void
  onShowMap: (routeId: string) => void
}) {
  const rowRouteKind = resolveRouteKind(route.route_kind)

  return (
    <div
      className={cn(
        isFirstStandby && "border-t-2 border-t-slate-400",
        "px-3 py-1.5 hover:bg-palette-jungleGreen000 transition-colors border-b border-palette-jungleGreen100 overflow-hidden cursor-pointer",
        route.status === "draft" && "bg-amber-50/50 border-l-4 border-l-amber-400",
        getRouteKindRowClasses(rowRouteKind),
        isHighlighted &&
          "ring-2 ring-amber-500 ring-inset bg-amber-100/50 animate-pulse",
      )}
      style={{ height: `${ROW_HEIGHT}px` }}
      onClick={(e) => {
        const target = e.target as HTMLElement
        if (!target.closest("button")) {
          if (onEdit) onEdit(route)
          else onShowMap(route.id)
        }
      }}
    >
      <div className="flex flex-col justify-center h-full relative z-10 gap-0.5">
        <div className="flex items-center justify-between gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-semibold text-palette-jungleGreen900 text-xs truncate">
                  {route.code}
                </span>
                {getRouteKindBadge(rowRouteKind) && (
                  <span className={ROUTE_KIND_BADGE_CLASSES}>
                    {getRouteKindBadge(rowRouteKind)}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs font-mono">
              {route.code}
            </TooltipContent>
          </Tooltip>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={() => onShowMap(route.id)}
              className="transition-colors p-0.5 rounded text-palette-jungleGreen400 hover:text-blue-600 hover:bg-blue-50"
            >
              <MapIcon size={11} />
            </button>
            {onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onEdit(route)}
                    className="transition-colors p-0.5 rounded text-palette-jungleGreen400 hover:text-amber-600 hover:bg-amber-50"
                  >
                    <Edit size={11} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Edit route (set Effective To date to end the route)
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-palette-jungleGreen500 overflow-hidden">
          {route.isRecurring && (
            <span className="text-[9px] px-1 py-0.5 rounded border flex-shrink-0 bg-purple-100 text-purple-700 border-purple-200 flex items-center gap-0.5">
              <Repeat size={7} />
              Recurring
            </span>
          )}
          <span
            className={cn(
              "text-[9px] px-1 py-0.5 rounded border flex-shrink-0",
              toneClasses(getPlannedRouteStatusDisplay(route.status).tone).badge,
            )}
          >
            {getPlannedRouteStatusDisplay(route.status).label}
          </span>
          <span className="text-palette-jungleGreen300 flex-shrink-0">•</span>
          <span className="truncate">{operator || "HIVED"}</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-palette-jungleGreen500 overflow-hidden">
          <Truck size={8} className="text-palette-jungleGreen400 flex-shrink-0" />
          <span className="truncate">{route.vehicleType || "HGV"}</span>
          {cost && cost.total_cost > 0 && (
            <>
              <span className="text-palette-jungleGreen300 flex-shrink-0">•</span>
              <span className="whitespace-nowrap font-medium text-blue-600">
                {formatCost(cost.total_cost)}
              </span>
            </>
          )}
          {loadFactor !== undefined && (
            <>
              <span className="text-palette-jungleGreen300 flex-shrink-0">•</span>
              <Package
                size={8}
                className="flex-shrink-0 text-palette-jungleGreen400"
              />
              <span
                className={cn(
                  "whitespace-nowrap font-medium",
                  loadFactor >= 100
                    ? "text-red-600"
                    : loadFactor >= 80
                      ? "text-amber-600"
                      : "text-green-600",
                )}
              >
                {loadFactor.toFixed(0)}%
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
